/*******************************************************************
* PAGE        : App
* REMARKS     :
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 23-02-2026
********************************************************************/

import { createTheme, ThemeProvider } from '@mui/material';
import { useEffect, useState } from 'react';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import LoaderComponent from './components/LoaderComponent/LoaderComponent';
import AppRoutes from './routes/AppRoutes';
import { fetchTheme } from './services/apiController';

/**
 * createAppTheme
 * -----------------
 * A utility function to create the theme for the app based on dynamic colors.
 * The theme is based on primary, secondary, and tertiary colors.
 */
const createAppTheme = (primaryColor, secondaryColor, tertiaryColor) => {
    return createTheme({
        typography: {
            fontFamily: "'Finlandica', sans-serif",
        },
        palette: {
            primary: { main: primaryColor, contrastText: '#FFF' },          // Primary color (e.g., blue)
            secondary: { main: secondaryColor, contrastText: '#FFF' },      // Secondary color (e.g., gray)
            tertiary: { main: tertiaryColor },                                // Tertiary color (used for backgrounds, etc.)
            textColor: { primaryText: '#206BB1', labelText: '#949699' },     // Custom text color
            whiteColor: '#FFFFFF',                                          // White color for various UI elements
            // borderColor: '#cccccc',                                         // Light border color
            borderColor: '#206BB1',                                         // Light border color
            backgroundColor: '#206BB1', 
            borderColorNew : '#90B5D8',                                 
            headerColor: '#e2e8f0',                                         // Light header background color
            widgetBackground: '#f5f5f5',                                    // Widget background color
            textColorWith75Opacity : "#5F6265",
            
        },
    });
};

const App = () => {
    // State to store the theme colors fetched from the API or default
    const [colors, setColors] = useState({ primary: '', secondary: '', tertiary: '' });

    // Fetch theme colors on component mount (default theme if the API fails)
    useEffect(() => {
        setColors({
            // primary: "#1976d2",     // Default primary color (blue) #1976d2
            primary: "#206BB1",     // Default primary color (blue) #1976d2
            secondary: "#6e6e6e",   // Default secondary color (gray)  #9e9e9e
            tertiary: "#f4f7fc",    // Default tertiary color (light background)
        });
        // Uncomment if you want to fetch theme dynamically from an API
        // getThemeData();
    }, []);

    /**
    * Fetches the theme colors from the API and updates the state.
    * If the API call fails, it falls back to default colors.
    */
    const getThemeData = async () => {
        try {
            const data = await fetchTheme();     // Fetch theme data from API

            setColors({
                primary: data[0].PrimaryColor,
                secondary: data[0].SecondaryColor,
                tertiary: data[0].TertiaryColor,
            });

        } catch (error) {
            console.error('Error fetching theme colors:', error);
        } finally {
            // Fallback to default colors in case of any failure
            setColors({
                // primary: "#1976d2",     //  Default primary color (blue) #1976d2
                primary: "#206BB1",     //  Default primary color (blue) #1976d2
                secondary: "#6e6e6e",   //  Default secondary color (gray) #9e9e9e
                tertiary: "#f4f7fc",    // Default tertiary color (light background)
            });
        }
    };

    // If the theme colors are not loaded yet, show the loader component
    if (!colors.primary || !colors.secondary || !colors.tertiary) {
        return <LoaderComponent />;
    }

    // Generate the app theme using the current colors
    const theme = createAppTheme(colors.primary, colors.secondary, colors.tertiary);

    // Set global CSS variables for dynamic theming
    document.documentElement.style.setProperty('--theme-background-color', theme.palette.tertiary.main || "#f4f7fc");
    document.documentElement.style.setProperty('--button-background-primary-color', theme.palette.primary.main);
    document.documentElement.style.setProperty('--button-text-primary-color', theme.palette.primary.contrastText);
    document.documentElement.style.setProperty('--button-background-secondary-color', theme.palette.secondary.main);
    document.documentElement.style.setProperty('--button-text-secondary-color', theme.palette.secondary.contrastText);
    document.documentElement.style.setProperty('--white-color', theme.palette.whiteColor);
    document.documentElement.style.setProperty('--border-color', theme.palette.borderColor);
    document.documentElement.style.setProperty('--background-color', theme.palette.backgroundColor);
    document.documentElement.style.setProperty('--header-background-color', theme.palette.headerColor);
    document.documentElement.style.setProperty('--label-text-color', theme.palette.textColor.labelText);
    document.documentElement.style.setProperty('--widget-background-color', theme.palette.widgetBackground);
    document.documentElement.style.setProperty('--border-color-new', theme.palette.borderColorNew);
    document.documentElement.style.setProperty('--text-color-with-75-opacity', theme.palette.textColorWith75Opacity);
    
    return (
        <div className="app-container">
            {/* Apply the theme using ThemeProvider */}
            <ThemeProvider theme={theme}>
                {/* ErrorBoundary to catch runtime errors */}
                <ErrorBoundary>
                    {/* Routes for navigating through the app */}
                    <AppRoutes />
                </ErrorBoundary>
            </ThemeProvider>
        </div>
    );
};

export default App;